import type { LeadPhotoDescriptor } from "./types.js";

/**
 * Secure handling for the optional photo upload. Since no real storage
 * backend exists yet (same "module boundary, no backend" situation as
 * LeadStore), this module's job is the part that matters regardless of
 * where files eventually land: reject anything that isn't actually an
 * image of an allowed type, enforce size/count limits, and never trust a
 * user-supplied file name for anything.
 */
export interface PhotoUploadCandidate {
  fileName: string;
  /** MIME type as reported by the browser/client - not trusted alone, see sniffImageMimeType. */
  declaredMimeType: string;
  sizeBytes: number;
  /** Raw file bytes (at least the first ~16 bytes are enough for signature sniffing). */
  data: Uint8Array;
}

export interface PhotoValidationError {
  code: string;
  message: string;
}

export interface PhotoValidationResult {
  valid: boolean;
  errors: PhotoValidationError[];
  descriptor?: Omit<LeadPhotoDescriptor, "id">;
}

export const ALLOWED_PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const MAX_PHOTO_BYTES = 8 * 1024 * 1024; // 8MB
export const MAX_PHOTOS_PER_LEAD = 6;

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp"
};

/**
 * Inspects the actual file bytes rather than trusting the declared MIME
 * type or file extension, which is what lets a malicious file disguised as
 * a photo (e.g. a script renamed to "vacation.jpg") be caught rather than
 * stored. Returns undefined for anything not recognized as one of the
 * allowed image types.
 */
export function sniffImageMimeType(data: Uint8Array): string | undefined {
  if (data.length >= 3 && data[0] === 0xff && data[1] === 0xd8 && data[2] === 0xff) {
    return "image/jpeg";
  }
  if (
    data.length >= 8 &&
    data[0] === 0x89 &&
    data[1] === 0x50 &&
    data[2] === 0x4e &&
    data[3] === 0x47 &&
    data[4] === 0x0d &&
    data[5] === 0x0a &&
    data[6] === 0x1a &&
    data[7] === 0x0a
  ) {
    return "image/png";
  }
  if (
    data.length >= 12 &&
    data[0] === 0x52 &&
    data[1] === 0x49 &&
    data[2] === 0x46 &&
    data[3] === 0x46 &&
    data[8] === 0x57 &&
    data[9] === 0x45 &&
    data[10] === 0x42 &&
    data[11] === 0x50
  ) {
    return "image/webp";
  }
  return undefined;
}

/** Strips path separators/control characters and caps length; the returned name is for display only - storage uses a random id, never this. */
function sanitizeDisplayFileName(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? "photo";
  // eslint-disable-next-line no-control-regex
  const withoutControlChars = base.replace(/[\x00-\x1f\x7f]/g, "");
  const stripped = withoutControlChars.replace(/[^A-Za-z0-9 ._-]/g, "").trim();
  const safe = stripped.length > 0 ? stripped : "photo";
  return safe.length > 150 ? safe.slice(0, 150) : safe;
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `photo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Validates one uploaded photo: allowed type (confirmed by content, not
 * just the declared MIME type or extension), size limit, and a sanitized
 * name for storage. Never throws - callers get a structured error list
 * they can show back to the user.
 */
export function validatePhotoUpload(candidate: PhotoUploadCandidate): PhotoValidationResult {
  const errors: PhotoValidationError[] = [];

  if (candidate.sizeBytes <= 0) {
    errors.push({ code: "photo_empty", message: `"${candidate.fileName}" appears to be empty.` });
  }
  if (candidate.sizeBytes > MAX_PHOTO_BYTES) {
    errors.push({
      code: "photo_too_large",
      message: `"${candidate.fileName}" is larger than the ${Math.round(MAX_PHOTO_BYTES / (1024 * 1024))}MB limit.`
    });
  }

  const detectedMimeType = sniffImageMimeType(candidate.data);
  if (!detectedMimeType || !ALLOWED_PHOTO_MIME_TYPES.includes(detectedMimeType as (typeof ALLOWED_PHOTO_MIME_TYPES)[number])) {
    errors.push({
      code: "photo_type_not_allowed",
      message: `"${candidate.fileName}" isn't a supported image type. Please upload a JPEG, PNG, or WEBP photo.`
    });
  } else if (
    ALLOWED_PHOTO_MIME_TYPES.includes(candidate.declaredMimeType as (typeof ALLOWED_PHOTO_MIME_TYPES)[number]) &&
    candidate.declaredMimeType !== detectedMimeType
  ) {
    // The declared type is a real image type but doesn't match what the
    // bytes actually are - treat as untrustworthy rather than guessing
    // which one is right.
    errors.push({
      code: "photo_type_mismatch",
      message: `"${candidate.fileName}" doesn't match its reported file type.`
    });
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  const extension = EXTENSION_BY_MIME_TYPE[detectedMimeType!] ?? "";
  return {
    valid: true,
    errors: [],
    descriptor: {
      originalFileName: sanitizeDisplayFileName(candidate.fileName),
      storedFileName: `${randomId()}${extension}`,
      mimeType: detectedMimeType!,
      sizeBytes: candidate.sizeBytes
    }
  };
}

export interface PhotoBatchValidationResult {
  valid: boolean;
  errors: PhotoValidationError[];
  descriptors: LeadPhotoDescriptor[];
}

/** Validates an entire batch of uploads at once, including the per-lead count limit. */
export function validatePhotoUploads(candidates: PhotoUploadCandidate[]): PhotoBatchValidationResult {
  if (candidates.length > MAX_PHOTOS_PER_LEAD) {
    return {
      valid: false,
      errors: [{ code: "too_many_photos", message: `You can attach up to ${MAX_PHOTOS_PER_LEAD} photos.` }],
      descriptors: []
    };
  }

  const errors: PhotoValidationError[] = [];
  const descriptors: LeadPhotoDescriptor[] = [];
  for (const candidate of candidates) {
    const result = validatePhotoUpload(candidate);
    if (!result.valid) {
      errors.push(...result.errors);
    } else if (result.descriptor) {
      descriptors.push({ id: randomId(), ...result.descriptor });
    }
  }

  return { valid: errors.length === 0, errors, descriptors };
}
